import { Injectable } from '@angular/core';
import { ColumnConfigFactory } from '@project/shared/grid/column-config-factory';
import { BaseAgGridSettingsService } from '@shared/ag-grid/base-ag-grid-settings.service';
import { CellAuditScoreComponent } from '@shared/ag-grid/component/cell-audit-score/cell-audit-score.component';
import { GridState } from '@shared/ag-grid/gird-state';
import { AgGridServicesWrapperService } from '@shared/ag-grid/services-wrapper.service';
import { UtilityService } from '@shared/services/utility.service';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { ProjectListType } from './project-list-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { ProjectStatusColors } from '@project/shared/project-status/status-styles.enum';
import { ProjectType } from '@project/shared/project-type.enum';

@Injectable()
export class SharedProjectListGridSettingService extends BaseAgGridSettingsService {
  public currentGridState: GridState = new GridState();
  public defaultGridState: GridState = new GridState();
  public initialGridState: GridState = new GridState();
  public _columnApi: ColumnApi;
  public _gridApi: GridApi;
  public _gridStateStorageKey: string;
  public program: any;
  public currentUserId: string;
  public listType: string;

  private column = {};
  private projectTypes = {};

  constructor(_agGridServicesWrapperService: AgGridServicesWrapperService,
              private _utilityService: UtilityService,
              private readonly translateService: TranslateService,
              private _columnConfigFactory: ColumnConfigFactory) {
    super(_agGridServicesWrapperService);
    this.column = _agGridServicesWrapperService._translateService.instant('projects.column');
    this.projectTypes = _agGridServicesWrapperService._translateService.instant('project.types');
  }

  // Call this from ngOnInit();
  public getGridOptions(listType: ProjectListType, projectStatuses: any[], initColumns = true): GridOptions {
    return <GridOptions>{
      columnDefs: initColumns ? this.getColumnSetting(listType, projectStatuses) : [],
      defaultColDef: {
        floatingFilter: true,
        sortable: true,
        filter: true
      },
      suppressRowClickSelection: true,
      rowMultiSelectWithClick: false,
      groupHideOpenParents: true,
      groupDefaultExpanded: -1,
      singleClickEdit: true,
      suppressDragLeaveHidesColumns: false,
      enableRangeSelection: true,
      rowSelection: 'multiple',
      components: {
        cellAuditScoreComponent: CellAuditScoreComponent
      },
      getRowHeight: (params) => {
        if (params.node.rowHeight > 80) {
          return params.node.rowHeight + 10;
        }
        return 80;
      },
      rowClass: 'row-with-tags',
      rowGroupPanelShow: 'always',
      autoGroupColumnDef: {
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          checkbox: true
        }
      },
      overlayLoadingTemplate: '<div class="loading-ring loading-ring-small"><div></div><div></div><div></div><div></div></div>',
      sideBar: {
        toolPanels: ['columns']
      },
      localeText: this.globalTranslations,
      context: {
        this: this
      }
    };
  }

  // Column Properties
  // Call from subscribe()
  public getColumnSetting(listType: ProjectListType, projectStatuses: any[]): ColDef[] {
    const columnConfigs = this._columnConfigFactory;
    const colDefs = [
      columnConfigs.get('checkbox'),
      {
        headerName: this.column['project-name'],
        field: 'name',
        headerTooltip: this.column['project-name'],
        unSortIcon: true,
        enablePivot: true,
        enableRowGroup: true,
        enableValue: true,
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (params.data) {
            if (params.data.projectType === ProjectType.credit) {
              let creditPath = 'milestones';
              if (params.data.zohoProjectId) {
                creditPath = 'overview';
              }
              return `<a class="hyperlink" href="#/oes/credit/shared-projects/${params.data.id}/${creditPath}">${params.data.name}</a><p style="opacity: .45; margin: 0;">${this.projectTypes[params.data?.projectType]}</p>`;
            } else {
              let path = 'planning/summary';
              if (listType === ProjectListType.CLAIMS) {
                path = 'connections';
              }
              return `<a class="hyperlink" href="#/oes/projects/shared-projects/${params.data.id}/${path}">${params.data.name}</a><p style="opacity: .45; margin: 0;">${this.projectTypes[params.data?.projectType]}</p>`;
            }
          }
        },
        pinned: 'left',
        filterParams: {
          caseSensitive: false,
          clearButton: true
        },
        suppressSizeToFit: true,
        resizable: true
      },
      {
        headerName: this.column['organization'],
        field: 'developer',
        headerTooltip: this.column['organization'],
        unSortIcon: true,
        enablePivot: true,
        enableRowGroup: true,
        enableValue: true,
        comparator: this.caseInsensitiveSort,
        filter: 'agTextColumnFilter',
        filterParams: {
          caseSensitive: false,
          clearButton: true
        },
        suppressSizeToFit: true
      },
      {
        ...columnConfigs.get('status'),
        editable: true,
        hide: true,
        width: 200,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: projectStatuses.map(status => {
            return status.id;
          })
        },
        valueFormatter: (params) => {
          const status = projectStatuses.find(s => s.id === params.value);
          return status ? this.translateService.instant(status.code) : params.value;
        },
        cellRenderer: (params) => {
          if (params?.value) {
            const projectStatus = projectStatuses.find(s => s.id === params.value);
            return `<div style="display: flex; align-items: center;">
              <div style="width: fit-content;
                display: flex;
                border-radius: 16px;
                line-height: 0;
                align-items: center;
                justify-content: center;
                padding: 4px 12px;
                height: 28px;
                background-color: ${ProjectStatusColors[projectStatus.id]};">
                ${this.translateService.instant(projectStatus.code)}
                <img
                  style="width: 10px; margin-left: 6px;"
                  src="./assets/icons/line-icons/shared/grey/arrow-down.svg" />
              </div>
            </div>`;
          }
        },
        resizable: true
      },
      columnConfigs.get('projectType'),
      columnConfigs.get('latitude'),
      columnConfigs.get('longitude'),
      columnConfigs.get('country'),
      columnConfigs.get('region'),
      columnConfigs.get('totalCustomers'),
      columnConfigs.get('totalPV'),
      columnConfigs.get('storage'),
      columnConfigs.get('generator'),
      columnConfigs.get('totalCapex'),
      columnConfigs.get('capexPerConnection'),
      columnConfigs.get('subsidyAmountPerConnection'),
      columnConfigs.get('lcoe'),
      columnConfigs.get('irr'),
      columnConfigs.get('equityIRR'),
      columnConfigs.get('payback'),
      columnConfigs.get('equityPayback'),
      columnConfigs.get('cod'),
      columnConfigs.get('submittedToProgramDateTime'),
      columnConfigs.get('arpuResidential'),
      columnConfigs.get('arpuCommercial'),
      columnConfigs.get('arpuPublic'),
      columnConfigs.get('arpuProductive'),
      columnConfigs.get('arpuAnchor'),
    ];

    colDefs.splice(3, 0, {
      headerName: this.column['stage'],
      field: 'projectStages',
      headerTooltip: this.column['stage'],
      unSortIcon: true,
      enablePivot: true,
      enableRowGroup: true,
      enableValue: true,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => this.getCurrentStageName(params.data?.projectStages),
      comparator: (a, b) => {
        try {
          const statusesA: any = JSON.parse(a);
          const orderedStatusesA = this._utilityService.orderJSONStatusesByDate(statusesA)
            .filter(status => status.timestamp != null)
            .map(status => status.name);

          const statusesB: any = JSON.parse(b);
          const orderedStatusesB = this._utilityService.orderJSONStatusesByDate(statusesB)
            .filter(status => status.timestamp != null)
            .map(status => status.name);

          const latestStatusA = orderedStatusesA ? orderedStatusesA[orderedStatusesA.length - 1] : null;
          const latestStatusB = orderedStatusesB ? orderedStatusesB[orderedStatusesB.length - 1] : null;

          return this.caseInsensitiveSort(latestStatusA, latestStatusB);
        } catch (jsonParseError: any) {
        }
      },
      filterParams: {
        caseSensitive: false,
        clearButton: true
      },
      suppressSizeToFit: false,
      cellRenderer: (params) => {
        if (params?.value) {
          const statuses: any = JSON.parse(params.value);
          const orderedStatuses = this._utilityService.orderJSONStatusesByDate(statuses);
          const currentStatus = orderedStatuses && orderedStatuses[orderedStatuses.length - 1]?.timestamp ? orderedStatuses[orderedStatuses.length - 1] : null;
          if (!currentStatus) {
            return '';
          }
          const textColor = this._utilityService.findTextColor(currentStatus.color);

          // Check if user is program owner
          let isProgramOwner = false;
          if (this.listType === 'MAIN') {
            // For MAIN list, use the programOwner flag from backend
            isProgramOwner = params.data.programOwner === true;
          } else {
            // For program-specific lists, check if user is program sponsor
            isProgramOwner = this.program?.sponsor?.id && this.currentUserId && this.program.sponsor.id === this.currentUserId;
          }

          const caretSvg = isProgramOwner ? `<svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L5 5L9 1" stroke="${textColor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>` : '';
          const cursorStyle = isProgramOwner ? 'cursor: pointer;' : '';

          return `<div style="display: flex; align-items: center; ${cursorStyle}">
                    <div style="width: fit-content;
                          display: flex;
                          gap: 8px;
                          border-radius: 15px;
                          line-height: 0;
                          align-items: center;
                          justify-content: center;
                          padding: 0 13px;
                          height: 30px;
                          color: ${textColor};
                          background-color: ${currentStatus.color};">${currentStatus.name}
                      ${caretSvg}
                    </div>
                  </div>`;
        }
      }
    });

    switch (listType) {
      case ProjectListType.ASSETS:
        colDefs.splice(30, 0, columnConfigs.get('latestCompletedMilestone'));
        colDefs.splice(4, 0, columnConfigs.get('auditScore'));
        break;
      case ProjectListType.CLAIMS:
        colDefs.splice(4, 0, columnConfigs.get('payConnections'));
        colDefs.splice(5, 0, columnConfigs.get('submittedConnections'));
        colDefs.splice(6, 0, columnConfigs.get('approvedConnections'));
        colDefs.splice(7, 0, columnConfigs.get('paidConnections'));
        colDefs.splice(8, 0, columnConfigs.get('rejectedConnections'));
        colDefs.splice(9, 0, columnConfigs.get('connectionsEverSubmitted'));
        colDefs.splice(10, 0, columnConfigs.get('totalPaymentAmount'));
        colDefs.splice(11, 0, columnConfigs.get('totalClaimAmount'));
        break;
      case ProjectListType.MAIN:
        colDefs.splice(4, 0, columnConfigs.get('program'));
        colDefs.splice(5, 0, columnConfigs.get('submittedToProgramDateTime'));
        colDefs.splice(7, 0, columnConfigs.get('latestCompletedMilestone'));
        colDefs.splice(13, 0, columnConfigs.get('submittedConnections'));
        colDefs.splice(14, 0, columnConfigs.get('approvedConnections'));
        colDefs.splice(15, 0, columnConfigs.get('paidConnections'));
        colDefs.splice(16, 0, columnConfigs.get('rejectedConnections'));
        colDefs.splice(17, 0, columnConfigs.get('connectionsEverSubmitted'));
        colDefs.splice(18, 0, columnConfigs.get('totalPaymentAmount'));
        colDefs.splice(19, 0, columnConfigs.get('totalClaimAmount'));
        break;
      default:
        break;
    }
    return colDefs;
  }

  // Name of the latest reached project application status, used as the filter value for the status column
  private getCurrentStageName(projectStages: string): string | null {
    if (!projectStages) {
      return null;
    }
    try {
      const reachedStatuses = this._utilityService.orderJSONStatusesByDate(JSON.parse(projectStages))
        .filter(status => status.timestamp != null);
      return reachedStatuses.length ? reachedStatuses[reachedStatuses.length - 1].name : null;
    } catch (jsonParseError: any) {
      return null;
    }
  }
}

